import React, { useState, useEffect, useRef, useId } from "react";
import { Col, Row } from "react-bootstrap";
import Webcam from "react-webcam";
import { Input } from "../Input";
import { images } from "../../config/images";
import {
  validateImage,
  getImagePreviewUrl,
  DEFAULT_MAX_IMAGE_SIZE,
} from "../../utilities/imageUtils";

interface ImageUploadProps {
  value?: any;
  onChange?: (image: File | string) => void;
  onError?: (error: string) => void;
  error?: string;
  disabled?: boolean;
  showWebcam?: boolean;
  maxSize?: number;
  label?: string;
  sizeLabel?: string;
  required?: boolean;
  register?: any;
  name?: string;
  uploadCol?: number;
  previewCol?: number;
  noWrapper?: boolean;
  labelAs?: "h6" | "small";
}

const ImageUpload = ({
  value,
  onChange,
  onError,
  error = "",
  disabled = false,
  showWebcam = true,
  maxSize = DEFAULT_MAX_IMAGE_SIZE,
  label = "Upload Latest Photo",
  sizeLabel,
  required = false,
  register,
  name = "image",
  uploadCol = 6,
  previewCol = 1,
  noWrapper = false,
  labelAs = "small",
}: ImageUploadProps) => {
  const uniqueId = useId();
  const inputId = `img-upload-${uniqueId}`;
  const [preview, setPreview] = useState("");
  const [liveCam, setLiveCam] = useState(false);
  const webcamRef = useRef<Webcam>(null);

  const computedSizeLabel =
    sizeLabel ||
    (maxSize >= 1048576
      ? `Maximum file size ${maxSize / 1048576}MB`
      : `Maximum file size ${maxSize / 1024} KB`);

  useEffect(() => {
    getImagePreviewUrl(value).then(setPreview);
  }, [value]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = validateImage(file, maxSize);
    if (!result.valid) {
      onError?.(result.error);
      return;
    }
    onError?.("");
    onChange?.(file);
  };

  const handleCapture = () => {
    const canvas = webcamRef.current?.getCanvas();
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `capture_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        onError?.("");
        onChange?.(file);
      }
    }, "image/jpeg");
    setLiveCam(false);
  };

  const handleRemove = () => {
    onChange?.("");
    setPreview("");
    setLiveCam(false);
    onError?.("");
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) input.value = "";
  };

  const hasImage = !!(value && value !== "");

  const uploadSection = (
    <Col lg={uploadCol}>
      <div className="text-start file-upload d-flex align-items-center">
        <img src={images.imgupload.default} alt="" />
        <span className="ms-3">
          {labelAs === "h6" ? (
            <h6 className="mb-0">
              {label} {required && <small className="text-danger">*</small>}
            </h6>
          ) : (
            <small className="mb-0 text-dark d-block fw-semibold">
              {label}
              {required && <small className="text-danger"> *</small>}
            </small>
          )}
          <small className="text-info">
            {computedSizeLabel} {required ? "" : "(optional)"}
          </small>
          <Input
            defaultValue=""
            type="file"
            placeholder=""
            id={inputId}
            name={name}
            className="form-control"
            onChange={handleFileChange}
            disabled={disabled}
            accept="image/png, image/jpeg"
            register={register}
          />
        </span>
      </div>
      {noWrapper && error && <div className="custom-error-msg">{error}</div>}
    </Col>
  );

  const previewSection = (
    <Col lg={previewCol} className="text-start">
      <div className="user-img">
        <img
          src={preview || images.imgupload.default}
          width="80"
          height="80"
          alt=""
          className="rounded-1"
        />
        {hasImage && !disabled ? (
          <span
            onClick={handleRemove}
            style={{ cursor: "pointer" }}
            title="Remove image"
          />
        ) : (
          showWebcam &&
          !liveCam &&
          !disabled && (
            <span
              className="bg-white img-camera-icon rounded-circle shadow-sm"
              onClick={() => setLiveCam(true)}
              title="Capture Live Pic"
            />
          )
        )}
      </div>
    </Col>
  );

  const webcamSection = showWebcam && !disabled && (
    <Col lg={5}>
      <div className="align-items-start d-flex flex-column">
        {liveCam && (
          <div className="user-img">
            <Webcam audio={false} ref={webcamRef} height="78" />
            <span
              className="bg-white img-camera-icon rounded-circle shadow-sm"
              title="Capture"
              onClick={handleCapture}
            />
          </div>
        )}
      </div>
    </Col>
  );

  const errorSection = !noWrapper && error && (
    <Col lg={12} className="mb-1">
      <span className="error-msg">{error}</span>
    </Col>
  );

  if (noWrapper) {
    return (
      <>
        {uploadSection}
        {previewSection}
      </>
    );
  }

  return (
    <Row className="mb-3">
      {uploadSection}
      {previewSection}
      {webcamSection}
      {errorSection}
    </Row>
  );
};

export { ImageUpload };
